import * as THREE from 'three';

/**
 * Procedural low-poly car made of coloured boxes.
 * All parts live in `this.group` which is added to the scene.
 */
export class CarMesh {
  constructor() {
    this.group = new THREE.Group();
    this._frontWheelPivots = [];
    this._allWheelMeshes   = [];
    this._build();
  }

  _build() {
    // ── Body ──────────────────────────────────────────────────────────────
    const bodyMat  = new THREE.MeshLambertMaterial({ color: 0x2255cc });
    const bodyGeo  = new THREE.BoxGeometry(1.9, 0.75, 4.2);
    const body     = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.y = 0.65;
    body.castShadow  = true;
    this.group.add(body);

    // ── Cabin ─────────────────────────────────────────────────────────────
    const cabinMat = new THREE.MeshLambertMaterial({ color: 0x1a3d8a });
    const cabinGeo = new THREE.BoxGeometry(1.7, 0.72, 2.4);
    const cabin    = new THREE.Mesh(cabinGeo, cabinMat);
    cabin.position.set(0, 1.42, -0.2);
    cabin.castShadow = true;
    this.group.add(cabin);

    // ── Windshields (semi-transparent) ────────────────────────────────────
    const glassMat = new THREE.MeshLambertMaterial({
      color: 0x88aadd, transparent: true, opacity: 0.45,
    });
    // Front windshield
    const fwGeo = new THREE.BoxGeometry(1.65, 0.62, 0.08);
    const fw    = new THREE.Mesh(fwGeo, glassMat);
    fw.position.set(0, 1.42, 0.92);
    this.group.add(fw);
    // Rear windshield
    const rwGeo = new THREE.BoxGeometry(1.65, 0.62, 0.08);
    const rw    = new THREE.Mesh(rwGeo, glassMat);
    rw.position.set(0, 1.42, -1.32);
    this.group.add(rw);

    // ── Headlights ────────────────────────────────────────────────────────
    const lightMat = new THREE.MeshBasicMaterial({ color: 0xffffcc });
    [-0.65, 0.65].forEach((xOff) => {
      const lGeo = new THREE.BoxGeometry(0.32, 0.18, 0.08);
      const lMesh = new THREE.Mesh(lGeo, lightMat);
      lMesh.position.set(xOff, 0.72, 2.15);
      this.group.add(lMesh);
    });

    // ── Tail lights ───────────────────────────────────────────────────────
    const tailMat = new THREE.MeshBasicMaterial({ color: 0xff2222 });
    [-0.65, 0.65].forEach((xOff) => {
      const tGeo = new THREE.BoxGeometry(0.28, 0.16, 0.08);
      const tMesh = new THREE.Mesh(tGeo, tailMat);
      tMesh.position.set(xOff, 0.72, -2.15);
      this.group.add(tMesh);
    });

    // ── Wheels ────────────────────────────────────────────────────────────
    const wheelGeo  = new THREE.CylinderGeometry(0.35, 0.35, 0.28, 18);
    // Rotate geometry so rolling axis aligns with car X (left-right)
    wheelGeo.rotateZ(Math.PI / 2);

    const hubGeo  = new THREE.CylinderGeometry(0.15, 0.15, 0.30, 8);
    hubGeo.rotateZ(Math.PI / 2);

    const tireMat = new THREE.MeshLambertMaterial({ color: 0x111111 });
    const hubMat  = new THREE.MeshLambertMaterial({ color: 0xaaaaaa });

    const wheelDefs = [
      { x: -1.05, y: 0.35, z:  1.5, front: true  }, // FL
      { x:  1.05, y: 0.35, z:  1.5, front: true  }, // FR
      { x: -1.05, y: 0.35, z: -1.5, front: false }, // RL
      { x:  1.05, y: 0.35, z: -1.5, front: false }, // RR
    ];

    for (const def of wheelDefs) {
      const tire = new THREE.Mesh(wheelGeo, tireMat);
      const hub  = new THREE.Mesh(hubGeo,  hubMat);
      tire.castShadow = true;

      const wheelGroup = new THREE.Group();
      wheelGroup.add(tire);
      wheelGroup.add(hub);

      if (def.front) {
        // Front wheels: wrapped in a pivot for steering
        const pivot = new THREE.Group();
        pivot.position.set(def.x, def.y, def.z);
        pivot.add(wheelGroup);
        this.group.add(pivot);
        this._frontWheelPivots.push(pivot);
        this._allWheelMeshes.push(wheelGroup);
      } else {
        wheelGroup.position.set(def.x, def.y, def.z);
        this.group.add(wheelGroup);
        this._allWheelMeshes.push(wheelGroup);
      }
    }
  }

  /**
   * Synchronise the 3D model to the current car physics state.
   * Call once per frame after car.update().
   *
   * @param {Object}   state   car.state snapshot
   * @param {Function} [elevFn] Optional: elevFn(x, z) → metres; used for Y + pitch
   */
  sync(state, elevFn) {
    const sinA  = Math.sin(state.angle);
    const cosA  = Math.cos(state.angle);
    const PROBE = 3; // metres

    // Y position: terrain height when grounded, altitude when flying
    const terrainY = elevFn ? elevFn(state.x, state.z) : 0;
    const carY     = state.flying ? (terrainY + state.y) : terrainY;

    // Pitch: only when grounded (flying = level)
    let pitch = 0;
    if (!state.flying && elevFn) {
      const hFront = elevFn(state.x + sinA * PROBE, state.z + cosA * PROBE);
      const hBack  = elevFn(state.x - sinA * PROBE, state.z - cosA * PROBE);
      pitch = Math.atan2(hBack - hFront, PROBE * 2);
    }

    // Position and heading — rotation order YXZ applies yaw then pitch correctly
    this.group.rotation.order = 'YXZ';
    this.group.position.set(state.x, carY, state.z);
    this.group.rotation.y = state.angle;
    this.group.rotation.x = pitch;

    // Wheel spin (all wheels rotate around their X axis = axle)
    for (const wg of this._allWheelMeshes) {
      wg.rotation.x = state.wheelRot;
    }

    // Front wheel steering pivot
    for (const pivot of this._frontWheelPivots) {
      pivot.rotation.y = state.wheelAngle;
    }
  }
}
